import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { catchError, EMPTY, exhaustMap, filter, from, map, mergeMap, of, takeUntil, tap, withLatestFrom } from "rxjs";
import { ConferenceMediaService } from "../data-access/conference-media.service";
import { ConferenceRoomApiService } from "../data-access/conference-room-api.service";
import { ConferenceActions } from "./conference.actions";
import { conferenceFeature } from "./conference.reducer";
import { SessionFacade } from "../../authentication";

@Injectable()
export class ConferenceEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly rooms = inject(ConferenceRoomApiService);
    private readonly media = inject(ConferenceMediaService);
    private readonly session = inject(SessionFacade);

    readonly create$ = createEffect(() => this.actions$.pipe(ofType(ConferenceActions.createRoomRequested), exhaustMap(() => this.rooms.create().pipe(
        map((roomId) => ConferenceActions.createRoomSucceeded({ roomId })),
        catchError(() => of(ConferenceActions.createRoomFailed({ error: "Conference room could not be created." }))),
    ))));
    readonly navigateCreated$ = createEffect(() => this.actions$.pipe(ofType(ConferenceActions.createRoomSucceeded), tap(({ roomId }) => void this.router.navigate(["/conference", roomId]))), { dispatch: false });
    readonly join$ = createEffect(() => this.actions$.pipe(ofType(ConferenceActions.joinRequested), exhaustMap(({ options }) => from(this.media.connect(options)).pipe(
        map((session) => ConferenceActions.joinSucceeded({ session, muted: !options.microphoneEnabled, cameraDisabled: !options.cameraEnabled })),
        catchError((error) => from(this.media.close()).pipe(map(() => ConferenceActions.joinFailed({ error: error instanceof Error ? error.message : "Conference connection failed." })))),
    ))));
    readonly mediaFlags$ = createEffect(() => this.actions$.pipe(ofType(ConferenceActions.muteToggled, ConferenceActions.cameraToggled), withLatestFrom(this.store.select(conferenceFeature.selectConferenceState)), exhaustMap(([action, state]) => action.type === ConferenceActions.muteToggled.type ? from(this.media.setMuted(state.muted)) : from(this.media.setCameraDisabled(state.cameraDisabled)))), { dispatch: false });
    readonly incomingMicrophoneMute$ = createEffect(() => this.media.microphoneMuted$.pipe(
        map(muted => ConferenceActions.microphoneMuteChanged({ muted }))
    ));
    readonly muteParticipant$ = createEffect(() => this.actions$.pipe(
        ofType(ConferenceActions.participantMuteRequested),
        withLatestFrom(this.store.select(conferenceFeature.selectConferenceState)),
        filter(([, state]) => state.phase === "active"),
        mergeMap(([{ participantId }]) => from(this.media.muteParticipant(participantId)).pipe(
            map(() => ConferenceActions.participantMuteSucceeded({ participantId })),
            catchError(() => of(ConferenceActions.participantMuteFailed({ participantId }))),
            takeUntil(this.actions$.pipe(ofType(ConferenceActions.leaveRequested, ConferenceActions.joinRequested)))
        ))
    ));
    readonly startScreenShare$ = createEffect(() => this.actions$.pipe(
        ofType(ConferenceActions.screenShareRequested),
        exhaustMap(() => from(this.media.startScreenShare()).pipe(
            map(() => ConferenceActions.screenShareStarted()),
            catchError(() => of(ConferenceActions.screenShareFailed())),
        )),
    ));
    readonly stopScreenShare$ = createEffect(() => this.actions$.pipe(
        ofType(ConferenceActions.screenShareStopRequested),
        withLatestFrom(this.store.select(conferenceFeature.selectConferenceState)),
        exhaustMap(([, state]) => from(this.media.stopScreenShare(state.cameraDisabled)).pipe(
            map(() => ConferenceActions.screenShareStopped()),
            catchError(() => of(ConferenceActions.screenShareFailed())),
        )),
    ));
    readonly browserStoppedScreenShare$ = createEffect(() => this.media.screenShareEnded$.pipe(map(() => ConferenceActions.screenShareStopRequested())));
    readonly pin$ = createEffect(() => this.actions$.pipe(ofType(ConferenceActions.pinRequested), exhaustMap(({ participantId }) => from(this.media.pinParticipant(participantId)).pipe(catchError(() => EMPTY)))), { dispatch: false });
    readonly incomingPin$ = createEffect(() => this.media.pinnedParticipant$.pipe(map((participantId) => ConferenceActions.pinChanged({ participantId }))));
    readonly activeSpeaker$ = createEffect(() => this.media.activeSpeaker$.pipe(map((participantId) => ConferenceActions.activeSpeakerChanged({ participantId }))));
    readonly changeDevices$ = createEffect(() => this.actions$.pipe(
        ofType(ConferenceActions.mediaDevicesChangeRequested),
        withLatestFrom(this.store.select(conferenceFeature.selectConferenceState)),
        exhaustMap(([{ videoDeviceId, audioDeviceId }, state]) => state.screenSharing
            ? of(ConferenceActions.mediaDevicesChangeFailed())
            : from(this.media.applyDevices(videoDeviceId, audioDeviceId, state.cameraDisabled, state.muted)).pipe(
                map(() => ConferenceActions.mediaDevicesChanged()),
                catchError(() => of(ConferenceActions.mediaDevicesChangeFailed())),
            )),
    ));
    readonly sendChatMessage$ = createEffect(() => this.actions$.pipe(
        ofType(ConferenceActions.chatMessageSendRequested),
        exhaustMap(({ text }) => from(this.media.sendChatMessage(text)).pipe(
            map((message) => ConferenceActions.chatMessageAdded({ message, incoming: false })),
            catchError(() => of(ConferenceActions.chatMessageSendFailed())),
        )),
    ));
    readonly incomingChatMessage$ = createEffect(() => this.media.chatMessage$.pipe(map((message) => ConferenceActions.chatMessageAdded({ message, incoming: true }))));
    readonly guestRequests$ = createEffect(() => this.media.guestRequests$.pipe(map((requests) => ConferenceActions.guestApprovalRequestsChanged({ requests }))));
    readonly decideGuest$ = createEffect(() => this.actions$.pipe(
        ofType(ConferenceActions.guestApprovalDecisionRequested),
        mergeMap(({ guestId, approved }) => from(this.media.decideGuest(guestId, approved)).pipe(
            catchError(() => EMPTY),
            takeUntil(this.actions$.pipe(ofType(ConferenceActions.leaveRequested, ConferenceActions.joinRequested)))
        ))
    ), { dispatch: false });
    readonly leave$ = createEffect(() => this.actions$.pipe(ofType(ConferenceActions.leaveRequested), exhaustMap(() => from(this.media.close()).pipe(map(() => ConferenceActions.leaveCompleted())))));
    readonly navigateAfterLeave$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ConferenceActions.leaveCompleted),
                tap(() => {
                    const user = this.session.user();
                    const returnUrl =
                        user?.role === "owner"
                            ? "/owner"
                            : user
                              ? "/dashboard/multiconference"
                              : "/multi/code";
                    void this.router.navigateByUrl(returnUrl);
                })
            ),
        { dispatch: false }
    );
}
